import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { collect } from 'collect.js';
import { Button, DatePicker, Flex, Table } from 'antd';
import { ExportOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import fileDownload from 'js-file-download';
import useApiClient from '../../../hooks/api/useApiClient';
import { TeamReportFormat, TeamReportType } from '../../../services/exports/Constants';
import useHelpers from '../../../hooks/context/useHelpers';
import ManagerSelect from '../../form/business/ManagerSelect';
import usePermissions, { ManagerRole } from '../../../hooks/auth/usePermissions';

export default function MrrReport() {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Team:Reports:MrrReport' });
  const { t: tRoles } = useTranslation(undefined, { keyPrefix: 'Shared:Roles' });

  const { TeamReportsManager } = useApiClient();

  const { profile } = useSelector((state) => state.initialData);

  const { formatCurrency } = useHelpers();
  const { isAdmin, hasAnyRole } = usePermissions();
  const isSalesLeadership = hasAnyRole([ManagerRole.SalesLeadership], false);

  const [loading, setLoading] = useState(true);
  const [selectedManagers, setSelectedManagers] = useState(isAdmin || isSalesLeadership ? [] : [profile.id]);
  const [month, setMonth] = useState(dayjs());

  const [report, setReport] = useState(null);
  const [details, setDetails] = useState({});
  const [loadingDetails, setLoadingDetails] = useState(null);

  useEffect(() => {
    selectedManagers?.length > 0 && loadReport(TeamReportFormat.JSON);
  }, [selectedManagers, month]);

  const onLoadManagers = ({ data, setManagers }) => {
    const filteredManagers = collect(data)
      .filter((item) =>
        collect(item.roles)
          .intersect([
            ManagerRole.SuperAdmin,
            ManagerRole.Admin,
            ManagerRole.Reseller,
            ManagerRole.SalesManager,
            ManagerRole.OnboardingManager,
            ManagerRole.SalesLeadership,
          ])
          .isNotEmpty(),
      )
      .toArray();

    setManagers(filteredManagers);
    const selectedIds = collect(filteredManagers)
      .filter((item) =>
        collect(item.roles)
          .intersect([
            ManagerRole.Reseller,
            ManagerRole.SalesManager,
            ManagerRole.OnboardingManager,
            ManagerRole.SalesLeadership,
          ])
          .isNotEmpty(),
      )
      .when(isSalesLeadership, (collection) =>
        collection.filter((item) => item.id === profile.id || !item.roles.includes(ManagerRole.SalesLeadership)),
      )
      .pluck('id')
      .toArray();

    setSelectedManagers(selectedIds);
    setLoading(false);
  };

  const loadReport = async (format) => {
    setDetails({});
    setLoading(true);
    const { success, data } = await TeamReportsManager.create({
      format,
      type: TeamReportType.MRR,
      start: month.startOf('month').toDate(),
      end: month.endOf('month').toDate(),
      managers: selectedManagers,
    });
    setLoading(false);

    if (!success) {
      toast.error(t('toasts.failed_to_load_data'));

      return null;
    }

    format === TeamReportFormat.JSON && setReport(data);

    return data;
  };

  const loadDetails = async (managerId) => {
    if (details[managerId]) {
      return;
    }

    setLoadingDetails(managerId);
    const { success, data } = await TeamReportsManager.loadManagerDetails({
      format: TeamReportFormat.JSON,
      type: TeamReportType.MRR,
      start: month.startOf('month').toDate(),
      end: month.endOf('month').toDate(),
      managers: [managerId],
    });
    setLoadingDetails(null);

    if (!success) {
      return toast.error(t('toasts.failed_to_load_data'));
    }

    return setDetails((current) => ({ ...current, [managerId]: data }));
  };

  const columns = [
    {
      title: t('fields.name'),
      key: 'name',
      dataIndex: 'manager_name',
    },
    {
      title: t('fields.area'),
      key: 'location',
      dataIndex: 'manager_location',
    },
    {
      title: t('fields.mrr'),
      key: 'mrr',
      render: (_, item) => formatCurrency(item.amount),
    },
  ];

  const renderDetails = (item) => (
    <Table
      columns={[
        { title: t('fields.name'), key: 'company_name', dataIndex: 'company_name' },
        { title: t('fields.area'), key: 'company_city', dataIndex: 'company_location' },
        {
          title: t('fields.mrr'),
          key: 'mrr',
          render: (_, item) => formatCurrency(item.amount),
        },
      ]}
      loading={loadingDetails === item.manager_id}
      dataSource={details[item.manager_id] ?? []}
      rowKey={(row) => `company-${row.id}`}
      pagination={false}
    />
  );

  return (
    <>
      <Flex justify="space-between">
        <h2>{t('title')}</h2>
        {report && (
          <Button
            type="primary"
            size="large"
            icon={<ExportOutlined />}
            loading={loading}
            onClick={() => {
              const filename = `MRR-report-${month.format('MMMM')}.csv`;
              loadReport(TeamReportFormat.File).then((data) => fileDownload(data, filename));
            }}
          >
            {t('buttons.download_report')}
          </Button>
        )}
      </Flex>
      <div className="mt-4 bg-background-inkWhite-white_0 shadow rounded-lg">
        <div className="flex lg:flex-row flex-col justify-between items-center gap-4 p-md border-b border-brand-inkGrey-grey_2">
          <div>
            <div className="w-full">
              <DatePicker value={month} onChange={setMonth} allowClear={false} picker="month" format="MMMM YYYY" />
            </div>
            {(isAdmin || isSalesLeadership) && (
              <ManagerSelect
                value={selectedManagers}
                isMulti
                roles={isSalesLeadership && !isAdmin ? [ManagerRole.SalesLeadership, ManagerRole.Reseller] : null}
                onLoad={onLoadManagers}
                formatOptions={(managers) =>
                  collect(managers)
                    .groupBy((item) => item.roles[0])
                    .map((managers, role) => ({
                      label: tRoles(role),
                      title: tRoles(role),
                      options: managers.map((item) => ({ label: item.name, value: item.id })).toArray(),
                    }))
                    .toArray()
                }
                onChange={(value) => setSelectedManagers(value)}
                className="mt-md"
              />
            )}
          </div>
          <div className="flex items-center justify-end">
            <Button size="large" icon={<ReloadOutlined />} onClick={() => loadReport(TeamReportFormat.JSON)} />
          </div>
        </div>
        <Table
          dataSource={report}
          columns={columns}
          pagination={false}
          loading={loading}
          expandable={{
            onExpand: (expanded, record) => expanded && loadDetails(record.manager_id),
            expandedRowRender: renderDetails,
            rowExpandable: (item) => item.amount > 0,
          }}
          className="rounded-none shadow-card bg-white overflow-hidden"
          rowKey={(item) => item.manager_id}
        />
      </div>
    </>
  );
}
